import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  FiArrowLeft,
  FiBriefcase,
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiSearch,
  FiUpload,
} from 'react-icons/fi';
import { API_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';
import ImageCropper, { COMPANY_HEADER_CROP_ASPECT } from '../../User/Pages/Components/froms/ImageCropper';
import './AdminPages.css';
import './CompanyList.css';

const headers = () => ({ Authorization: `Bearer ${getAuthToken()}` });
const PAGE_SIZE = 12;

const emptyForm = () => ({
  name: '',
  description: '',
  logo: '',
  header_image: '',
  show_default_header_image: true,
  is_active: true,
  property_ids: [],
  project_ids: [],
});

const formatPrice = (price) => {
  if (price == null || price === '') return null;
  const n = Number(price);
  if (!Number.isFinite(n)) return null;
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  return `₹${n.toLocaleString('en-IN')}`;
};

const CompanyFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const editingId = id ? Number(id) : null;
  const isEdit = Number.isFinite(editingId) && editingId > 0;

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [listingKind, setListingKind] = useState('property');
  const [listingQuery, setListingQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [available, setAvailable] = useState([]);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const searchTimer = useRef(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropFileName, setCropFileName] = useState('');
  const [cropTarget, setCropTarget] = useState(null); // 'header_image' | null
  const [uploadingHeader, setUploadingHeader] = useState(false);

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedQuery(listingQuery.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [listingQuery]);

  const loadDetail = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/companies/${editingId}`, { headers: headers() });
      setForm({
        name: res.data?.company?.name || '',
        description: res.data?.company?.description || '',
        logo: res.data?.company?.logo || '',
        header_image: res.data?.company?.header_image || '',
        show_default_header_image: res.data?.company?.show_default_header_image !== false,
        is_active: res.data?.company?.is_active !== false,
        property_ids: (res.data?.properties || []).map((p) => p.id),
        project_ids: (res.data?.projects || []).map((p) => p.id),
      });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to load company');
      navigate('/admin/companies');
    } finally {
      setLoading(false);
    }
  }, [editingId, isEdit, navigate]);

  const loadAvailable = useCallback(async () => {
    setAvailableLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/companies/available-listings`, {
        headers: headers(),
        params: {
          kind: listingKind === 'all' ? 'all' : listingKind,
          q: debouncedQuery || undefined,
          page,
          limit: PAGE_SIZE,
        },
      });
      setAvailable(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotal(Number(res.data?.total) || 0);
      setPages(Math.max(1, Number(res.data?.pages) || 1));
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to load listings');
      setAvailable([]);
      setTotal(0);
      setPages(1);
    } finally {
      setAvailableLoading(false);
    }
  }, [listingKind, debouncedQuery, page]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    loadAvailable();
  }, [loadAvailable]);

  useEffect(() => {
    setPage(1);
  }, [listingKind]);

  const toggleListing = (item) => {
    const assignedElsewhere = item.company_id
      && Number(item.company_id) > 0
      && (!isEdit || Number(item.company_id) !== Number(editingId));
    if (assignedElsewhere) return;

    const key = item.is_project ? 'project_ids' : 'property_ids';
    setForm((prev) => {
      const set = new Set(prev[key]);
      if (set.has(item.id)) set.delete(item.id);
      else set.add(item.id);
      return { ...prev, [key]: [...set] };
    });
  };

  const selectedSet = useMemo(() => {
    return new Set([...(form.property_ids || []), ...(form.project_ids || [])]);
  }, [form.property_ids, form.project_ids]);

  const selectedCount = selectedSet.size;

  const logoSrc = (logo) => {
    if (!logo) return '';
    if (/^https?:\/\//i.test(logo)) return logo;
    const base = API_URL.replace(/\/api\/?$/, '');
    const path = String(logo).replace(/^\/+/, '');
    return `${base}/${path.startsWith('uploads/') ? path : `uploads/${path}`}`;
  };

  const uploadImage = async (file, field, successMsg) => {
    if (!file) return;
    const fm = new FormData();
    fm.append('file', file);
    try {
      if (field === 'header_image') setUploadingHeader(true);
      const res = await axios.post(`${API_URL}/upload/single`, fm, {
        headers: { ...headers(), 'Content-Type': 'multipart/form-data' },
      });
      const path = res.data?.path || res.data?.filename || res.data?.url || '';
      setForm((prev) => ({ ...prev, [field]: path }));
      toast.success(successMsg);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Image upload failed');
    } finally {
      if (field === 'header_image') setUploadingHeader(false);
    }
  };

  const openHeaderCrop = (file) => {
    if (!file || !file.type?.startsWith('image/')) return;
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropFileName(file.name || 'header.jpg');
    setCropTarget('header_image');
    setCropSrc(URL.createObjectURL(file));
  };

  const closeHeaderCrop = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    setCropFileName('');
    setCropTarget(null);
  };

  const handleHeaderCropDone = async (croppedFile) => {
    closeHeaderCrop();
    await uploadImage(croppedFile, 'header_image', 'Header image uploaded');
  };

  const saveCompany = async () => {
    if (!String(form.name || '').trim()) {
      toast.error('Company name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || '',
        logo: form.logo || '',
        header_image: form.header_image || '',
        show_default_header_image: form.show_default_header_image !== false,
        is_active: form.is_active !== false,
        property_ids: form.property_ids || [],
        project_ids: form.project_ids || [],
        replace_assignments: true,
      };
      if (isEdit) {
        const res = await axios.put(`${API_URL}/admin/companies/${editingId}`, payload, { headers: headers() });
        if (res.data?.company?.duplicate_pending) {
          toast.success('Company updated as a separate record (duplicate name flagged for review)');
        } else {
          toast.success('Company updated');
        }
      } else {
        const res = await axios.post(`${API_URL}/admin/companies`, payload, { headers: headers() });
        if (res.data?.company?.duplicate_pending) {
          toast.success('Created as a new separate company (duplicate name flagged for review)');
        } else {
          toast.success('Company created');
        }
      }
      navigate('/admin/companies');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save company');
    } finally {
      setSaving(false);
    }
  };

  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="admin-page company-list-page company-form-page">
      {cropSrc && cropTarget === 'header_image' ? (
        <ImageCropper
          src={cropSrc}
          fileName={cropFileName}
          aspect={COMPANY_HEADER_CROP_ASPECT}
          title="Crop Header Image"
          onDone={handleHeaderCropDone}
          onCancel={closeHeaderCrop}
        />
      ) : null}
      <div className="page-header">
        <div>
          <h1>
            <FiBriefcase style={{ marginRight: 8 }} />
            {isEdit ? 'Edit Company' : 'Add New Company'}
          </h1>
          <p>
            {isEdit
              ? 'Update company details and assign properties or projects.'
              : 'Create a company/developer and assign properties or projects.'}
          </p>
        </div>
        <div className="page-header-actions">
          <Link to="/admin/companies" className="btn-secondary" style={{ textDecoration: 'none' }}>
            <FiArrowLeft /> Back to Company List
          </Link>
        </div>
      </div>

      <div className="admin-card company-form-shell">
        {loading ? (
          <div className="skeleton" style={{ height: 320, borderRadius: 8 }} />
        ) : (
          <>
            <section className="company-form-section">
              <h2 className="company-form-section-title">Company details</h2>
              <div className="company-form-grid">
                <label>
                  <span>Company / Developer Name</span>
                  <input
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Prestige Group"
                  />
                </label>
                <label>
                  <span>Logo</span>
                  <div className="company-logo-upload company-logo-upload--row">
                    <div className="company-logo-preview">
                      {form.logo ? (
                        <img src={logoSrc(form.logo)} alt="" />
                      ) : (
                        <span className="company-logo-placeholder"><FiUpload /> Upload</span>
                      )}
                    </div>
                    <label className="company-logo-file-btn">
                      {form.logo ? 'Change logo' : 'Choose logo'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          uploadImage(e.target.files?.[0], 'logo', 'Logo uploaded');
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                </label>
                <label>
                  <span>Header image (right side)</span>
                  <div className="company-logo-upload company-logo-upload--row company-header-upload">
                    <div className="company-header-preview">
                      {form.header_image ? (
                        <img src={logoSrc(form.header_image)} alt="" />
                      ) : (
                        <span className="company-logo-placeholder">
                          <FiUpload /> {uploadingHeader ? 'Uploading…' : 'Upload & crop'}
                        </span>
                      )}
                    </div>
                    <label className="company-logo-file-btn">
                      {form.header_image ? 'Change header' : 'Choose header'}
                      <input
                        type="file"
                        accept="image/*"
                        disabled={uploadingHeader}
                        onChange={(e) => {
                          openHeaderCrop(e.target.files?.[0]);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                  <small className="company-form-hint">
                    Crop to a 2:1 frame matched to the right-side header panel. If empty, the admin default may be used.
                  </small>
                </label>
                <label className="company-form-toggle">
                  <span>Show Default Image</span>
                  <div className="company-toggle-row">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={form.show_default_header_image !== false}
                      className={`company-toggle${form.show_default_header_image !== false ? ' is-on' : ''}`}
                      onClick={() => setForm((p) => ({
                        ...p,
                        show_default_header_image: !(p.show_default_header_image !== false),
                      }))}
                    >
                      <span className="company-toggle-knob" />
                    </button>
                    <em>{form.show_default_header_image !== false ? 'On' : 'Off'}</em>
                  </div>
                  <small className="company-form-hint">
                    When no custom header image is set, show the default header image from Company List settings.
                  </small>
                </label>
                <label className="company-form-full">
                  <span>Description</span>
                  <textarea
                    rows={3}
                    value={form.description}
                    onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                    placeholder="Short description about the company / developer"
                  />
                </label>
              </div>
            </section>

            <section className="company-form-section">
              <div className="company-form-section-head">
                <div>
                  <h2 className="company-form-section-title">Assign listings</h2>
                  <p className="company-form-section-sub">
                    Select properties and projects to link with this company.
                  </p>
                </div>
                <div className="company-assign-meta company-assign-meta--pill">
                  <strong>{selectedCount}</strong> selected
                  <span>·</span>
                  {form.property_ids?.length || 0} properties
                  <span>·</span>
                  {form.project_ids?.length || 0} projects
                </div>
              </div>

              <div className="company-assign-toolbar">
                <div className="company-kind-tabs">
                  {[
                    { id: 'property', label: 'Properties' },
                    { id: 'project', label: 'Projects' },
                    { id: 'all', label: 'All' },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      className={listingKind === tab.id ? 'is-active' : ''}
                      onClick={() => setListingKind(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                <div className="company-search company-search--wide">
                  <FiSearch />
                  <input
                    value={listingQuery}
                    onChange={(e) => setListingQuery(e.target.value)}
                    placeholder="Search by name, ID, location, company…"
                    aria-label="Search listings"
                  />
                </div>
              </div>

              <div className="company-card-grid company-card-grid--form">
                {availableLoading && (
                  <>
                    {[1, 2, 3, 4, 5, 6].map((n) => (
                      <div key={n} className="skeleton company-listing-card-skel" />
                    ))}
                  </>
                )}
                {!availableLoading && available.length === 0 && (
                  <div className="company-empty company-empty--wide">
                    No listings found{debouncedQuery ? ` for “${debouncedQuery}”` : ''}.
                  </div>
                )}
                {!availableLoading && available.map((item) => {
                  const selected = selectedSet.has(item.id);
                  const price = formatPrice(item.price);
                  const assignedElsewhere = item.company_id
                    && Number(item.company_id) > 0
                    && (!isEdit || Number(item.company_id) !== Number(editingId));
                  const assignedTip = assignedElsewhere
                    ? `Already assigned to ${item.company_name || 'another company'}`
                    : undefined;
                  return (
                    <button
                      key={`${item.is_project ? 'proj' : 'prop'}-${item.id}`}
                      type="button"
                      disabled={assignedElsewhere}
                      title={assignedTip}
                      aria-disabled={assignedElsewhere}
                      className={`company-listing-card company-listing-card--form is-selectable${selected ? ' is-selected' : ''}${assignedElsewhere ? ' is-assigned-elsewhere' : ''}`}
                      onClick={() => toggleListing(item)}
                    >
                      <div className="company-listing-thumb">
                        {item.thumbnail ? (
                          <img src={item.thumbnail} alt="" loading="lazy" />
                        ) : (
                          <span>No image</span>
                        )}
                        <span className={`company-listing-badge${item.is_project ? ' is-project' : ''}`}>
                          {item.is_project ? 'Project' : 'Property'}
                        </span>
                        {selected && (
                          <span className="company-check"><FiCheck /></span>
                        )}
                        {assignedElsewhere && (
                          <span className="company-assigned-overlay" title={assignedTip}>
                            Assigned
                          </span>
                        )}
                      </div>
                      <div className="company-listing-body">
                        <strong title={item.title}>{item.title}</strong>
                        <small>
                          #{item.property_id}
                          {item.location ? ` · ${item.location}` : ''}
                        </small>
                        <div className="company-listing-meta-row">
                          {price ? <span className="company-listing-price">{price}</span> : <span />}
                          {item.company_name ? (
                            <span className="company-listing-co" title={item.company_name}>
                              {item.company_name}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="company-pagination">
                <span className="company-pagination-info">
                  {total === 0 ? '0 listings' : `Showing ${from}–${to} of ${total}`}
                </span>
                <div className="company-pagination-btns">
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={page <= 1 || availableLoading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <FiChevronLeft /> Prev
                  </button>
                  <span className="company-pagination-page">Page {page} / {pages}</span>
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={page >= pages || availableLoading}
                    onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  >
                    Next <FiChevronRight />
                  </button>
                </div>
              </div>
            </section>

            <div className="company-editor-actions">
              <button type="button" className="btn-secondary" onClick={() => navigate('/admin/companies')} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={saveCompany} disabled={saving}>
                {saving ? 'Saving…' : (isEdit ? 'Save Company' : 'Create Company')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CompanyFormPage;
